import { formatAmountWithUnit, parseAmount } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { amountInputLabel, amountInputStep, amountInputValue, useCurrency } from "../lib/currency";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

type Direction = "add" | "remove";

/**
 * Signed balance adjustment from the customer page (boxed parity: Account Balance Adjustment).
 * Add or remove an amount typed in the environment's currency, with a required note that becomes
 * the ledger row's reference. A removal is refused below zero on the server; the preview here
 * says so first. Editors and admins only.
 */
export function AdjustBalanceControl({
  endUserId,
  balanceCents,
}: {
  endUserId: string;
  balanceCents: number;
}) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const currency = useCurrency();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState<Direction>("add");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const reset = () => {
    setDirection("add");
    setAmount("");
    setNote("");
  };

  const adjust = useMutation(
    trpc.users.adjustBalance.mutationOptions({
      onSuccess: (_result, variables) => {
        const shown = formatAmountWithUnit(Math.abs(variables.amountCents), currency);
        toast.success(variables.amountCents > 0 ? `Added ${shown}.` : `Removed ${shown}.`);
        setOpen(false);
        reset();
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.stats.pathKey() });
      },
    }),
  );

  const magnitude = parseAmount(amount, currency);
  const signed = magnitude === null ? null : direction === "add" ? magnitude : -magnitude;
  const after = signed === null ? null : balanceCents + signed;
  const overdrawn = after !== null && after < 0;
  const valid = signed !== null && signed !== 0 && note.trim().length > 0 && !overdrawn;

  const submit = () => {
    if (signed === null || !valid) return;
    adjust.mutate({
      endUserId,
      amountCents: signed,
      note: note.trim(),
      idempotencyKey: crypto.randomUUID(),
    });
  };

  if (!can("editor")) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger render={<Button type="button" variant="outline" size="sm" />}>
        Adjust balance
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust balance</DialogTitle>
          <DialogDescription>
            Adds to or removes from this user's balance right away and writes one ledger entry with
            your note as its reference. A removal can dip below what pending battles hold, never
            below zero.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="sr-only">Direction</legend>
            <Button
              type="button"
              variant={direction === "add" ? "default" : "outline"}
              aria-pressed={direction === "add"}
              onClick={() => setDirection("add")}
            >
              Add
            </Button>
            <Button
              type="button"
              variant={direction === "remove" ? "destructive" : "outline"}
              aria-pressed={direction === "remove"}
              onClick={() => setDirection("remove")}
            >
              Remove
            </Button>
          </fieldset>
          <div className="grid gap-2">
            <Label htmlFor="adjust-amount">{amountInputLabel("Amount", currency)}</Label>
            <Input
              id="adjust-amount"
              type="number"
              min={amountInputStep(currency)}
              step={amountInputStep(currency)}
              placeholder={amountInputValue(500, currency)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="adjust-note">Note</Label>
            <Input
              id="adjust-note"
              maxLength={256}
              placeholder="Why, in a few words"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
            />
          </div>
          <p className={`text-sm ${overdrawn ? "text-destructive" : "text-muted-foreground"}`}>
            Balance after: {after === null ? "—" : formatAmountWithUnit(after, currency)}
            {overdrawn ? " · more than the balance holds" : ""}
          </p>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button
              type="submit"
              variant={direction === "remove" ? "destructive" : "default"}
              disabled={adjust.isPending || !valid}
            >
              {adjust.isPending ? "Saving…" : direction === "add" ? "Add" : "Remove"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
